import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { usePageSEO } from '../hooks/usePageSEO';
import ResumePreview from '../components/ResumePreview';
import { TEMPLATES } from '../data/samples';
import { MarketingHeader, MarketingFooter, LandingStyles, buildShowcaseResume } from './OtherPages';
import {
  ArrowRight, Target, BarChart2, FileText, Search, Sparkles, ClipboardList, Check,
  X, ChevronDown, ChevronLeft, ChevronRight, Quote, Printer, Briefcase, FolderGit2,
  Wrench, GraduationCap, BadgeCheck, Eye,
} from 'lucide-react';

// ── Data ───────────────────────────────────────────────────────────────────────
const SCORE_ITEMS = [
  { label: 'Structure', status: 'good', note: 'Good' },
  { label: 'ATS Friendly', status: 'good', note: 'Good' },
  { label: 'Keywords', status: 'bad', note: 'Missing' },
  { label: 'Formatting', status: 'good', note: 'Good' },
  { label: 'Impact', status: 'warn', note: 'Medium' },
  { label: 'Achievements', status: 'bad', note: 'Weak' },
];

const IMPROVEMENT_CARDS = [
  { Icon: Target, title: 'Tailor Every Resume', text: 'Match your resume with every job description.', cta: 'Learn', to: '/ats-guide' },
  { Icon: BarChart2, title: 'Strong Achievement Bullets', text: 'Replace responsibilities with measurable results.', cta: 'Improve', to: '/build/resume' },
  { Icon: FileText, title: 'ATS Formatting', text: 'Build resumes recruiters can scan instantly.', cta: 'Learn', to: '/ats-guide' },
  { Icon: Search, title: 'Keyword Optimization', text: 'Find missing keywords before applying.', cta: 'Check', to: '/ats-checker' },
  { Icon: Sparkles, title: 'Professional Summary', text: 'Write summaries recruiters actually read.', cta: 'Generate', to: '/build/resume' },
  { Icon: ClipboardList, title: 'Final Resume Review', text: 'Check everything before exporting.', cta: 'Review', to: '/ats-checker' },
];

const BEFORE_AFTER = [
  { before: 'Responsible for customer support.', after: 'Resolved 60+ customer tickets weekly while improving first-response time by 28%.' },
  { before: 'Worked on React projects.', after: 'Built reusable React components reducing page load by 35%.' },
  { before: 'Managed social media.', after: 'Increased engagement by 54% through targeted campaigns.' },
];

const RECRUITER_RULES = [
  { title: 'Show measurable impact', caption: 'Use real numbers — %, $, time saved, team size.' },
  { title: 'Keep it one page (early career)', caption: 'Two pages only once you have 8+ years of experience.' },
  { title: 'Tailor every application', caption: 'Mirror the job description’s language and priorities.' },
  { title: 'Start bullets with action verbs', caption: '"Led", "Built", "Reduced" — not "Responsible for".' },
  { title: 'Avoid graphics', caption: 'Skill bars and icons often get skipped by parsers.' },
  { title: 'Use ATS headings', caption: 'Work Experience, Education, Skills — nothing creative.' },
  { title: 'Avoid keyword stuffing', caption: 'Use each keyword naturally, inside real context.' },
  { title: 'Use a professional email', caption: 'firstname.lastname@email.com, not a nickname.' },
  { title: 'Use reverse chronological order', caption: 'Most recent role first — it’s what recruiters expect.' },
  { title: 'Export as PDF', caption: 'Preserves formatting across every device and system.' },
];

const MISTAKE_PAIRS = [
  { category: 'Summary', mistake: 'I am a hardworking professional looking for opportunities to grow.', better: 'Software Engineer with 4 years of React experience building SaaS platforms.' },
  { category: 'Skills', mistake: 'Microsoft Word, team player, fast learner, hard worker.', better: 'React, TypeScript, PostgreSQL, AWS, CI/CD pipelines.' },
  { category: 'Experience', mistake: 'Responsible for various tasks and duties as assigned.', better: 'Led migration of a legacy system to microservices, cutting deployment time by 60%.' },
  { category: 'Projects', mistake: 'Made a website for a class project.', better: 'Built a full-stack e-commerce app with Stripe integration, used by 200+ beta testers.' },
  { category: 'Achievements', mistake: 'Did a good job and was recognized by management.', better: 'Awarded Employee of the Quarter for reducing support backlog by 40%.' },
  { category: 'Education', mistake: 'Some college coursework, still learning new things.', better: 'B.S. Computer Science, University of Washington — GPA 3.8, Dean’s List.' },
];

const SECTION_CHECKLIST = [
  { Icon: Sparkles, title: 'Professional Summary', expect: '2-3 sentences naming your role, years of experience, and top strength.', mistake: 'Generic filler like "hardworking team player."' },
  { Icon: Briefcase, title: 'Experience', expect: 'Action verb + scope + measurable result in every bullet.', mistake: 'Listing duties instead of outcomes.' },
  { Icon: FolderGit2, title: 'Projects', expect: 'What you built, the tools used, and the impact or reach.', mistake: 'No mention of technologies or results.' },
  { Icon: Wrench, title: 'Skills', expect: 'Specific tools and technologies grouped by category.', mistake: 'Vague soft skills with no hard skills.' },
  { Icon: GraduationCap, title: 'Education', expect: 'Degree, school, and graduation year — GPA if strong.', mistake: 'Outdated coursework taking up valuable space.' },
  { Icon: BadgeCheck, title: 'Certifications', expect: 'Relevant, current certifications tied to the target role.', mistake: 'Expired or unrelated certifications.' },
];

const ATS_CHECKLIST_ITEMS = ['Contact details', 'ATS headings', 'Keywords', 'Numbers', 'Action verbs', 'PDF export', 'Professional filename', 'No graphics'];

const AI_EXAMPLES = [
  { weak: 'Responsible for managing a team.', suggestion: 'Add team size and a measurable outcome.', approved: 'Led a cross-functional team of 8, improving on-time delivery by 30%.' },
  { weak: 'Helped with customer support tasks.', suggestion: 'Add volume, speed, or satisfaction metrics.', approved: 'Resolved 60+ customer tickets weekly while improving first-response time by 28%.' },
  { weak: 'Worked on marketing campaigns.', suggestion: 'Show reach or performance gains.', approved: 'Launched campaigns that increased engagement by 54% across social channels.' },
];

const RESUME_EXAMPLE_ROLES = [
  { role: 'Software Engineer', templateId: 'tech', score: 94 },
  { role: 'Marketing Manager', templateId: 'elegant', score: 90 },
  { role: 'Product Manager', templateId: 'modern', score: 92 },
];

const RECRUITER_QUOTES = [
  { quote: 'I spend around 7 seconds on the first review.', role: 'Senior Recruiter', company: 'Google' },
  { quote: 'I skip resumes with generic summaries.', role: 'Talent Acquisition Lead', company: '' },
  { quote: 'Achievements always stand out more than duties.', role: 'Hiring Manager', company: '' },
];

const FAQS = [
  ['How long should my resume be?', 'One page if you have under 8-10 years of experience. Two pages is acceptable once you have significant, relevant history — never pad length just to fill space.'],
  ['Should I include hobbies?', 'Only if they are relevant to the role or say something specific about you — an open-source project or leadership in a club, for example. Generic hobbies rarely add value.'],
  ['How many bullet points per role?', '3-5 bullets for recent, relevant roles. Older or less relevant roles can be trimmed to 1-2 lines.'],
  ['How many skills should I list?', 'Around 8-12 specific, relevant skills grouped by category. Long lists of generic skills dilute the ones that actually matter for the role.'],
  ['PDF or DOCX?', 'PDF, unless the application explicitly asks for a Word document. PDF preserves formatting reliably and most modern ATS platforms parse it correctly.'],
  ['Do recruiters actually read summaries?', 'Yes, especially in the first few seconds of a scan. A focused, specific summary is one of the highest-leverage sections on your resume.'],
  ['Should every application use the same resume?', 'No. Keep one strong base resume, then tailor a copy for each role using that job description’s language and priorities.'],
  ['How important are keywords?', 'Very. ATS software ranks resumes by keyword match before a human ever sees them — missing key terms can filter you out automatically.'],
  ['How often should I update my resume?', 'Every time you finish a notable project or take on new responsibilities — not just when you start job hunting.'],
  ['Can AI actually improve my resume?', 'Yes, when it works from your real experience. MarsResume’s AI rewrites bullets, generates summaries, and tailors content to a job description — you always review the result.'],
];

// ── Small shared pieces ───────────────────────────────────────────────────────
const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <div className="rtp-sec-heading">
    {eyebrow && <div className="rtp-eyebrow">{eyebrow}</div>}
    <h2>{title}</h2>
    {subtitle && <p>{subtitle}</p>}
  </div>
);

const STATUS_STYLES = {
  good: { color: '#059669', bg: '#ECFDF5', Icon: Check },
  warn: { color: '#D97706', bg: '#FFFBEB', Icon: Check },
  bad: { color: '#EF1D28', bg: '#FFF1F2', Icon: X },
};

// ── Hero ───────────────────────────────────────────────────────────────────────
const HeroSection = ({ user, navigate }) => (
  <section className="rtp-hero">
    <div className="rtp-hero-grid-bg" />
    <div className="rtp-hero-glow" />
    <div className="rtp-hero-inner">
      <div className="rtp-hero-left">
        <h1>Resume Tips That Help You Get More Interviews.</h1>
        <p>Learn practical resume strategies used by recruiters and ATS systems. Improve every section of your resume before you apply.</p>
        <div className="rtp-hero-ctas">
          <button type="button" className="rtp-btn-primary" onClick={() => navigate(user ? '/dashboard' : '/build/resume')}>Improve My Resume <ArrowRight size={16} /></button>
          <button type="button" className="rtp-btn-ghost-dark" onClick={() => navigate('/templates')}>Browse Templates</button>
        </div>
      </div>
      <div className="rtp-hero-right">
        <div className="rtp-score-card">
          <div className="rtp-score-card-top">
            <div>
              <div className="rtp-score-card-label">Resume Score</div>
              <div className="rtp-score-card-value">74%</div>
            </div>
            <svg width="72" height="72" viewBox="0 0 72 72">
              <circle cx="36" cy="36" r="30" fill="none" stroke="#F1F5F9" strokeWidth="8" />
              <circle cx="36" cy="36" r="30" fill="none" stroke="#EF1D28" strokeWidth="8" strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 30} strokeDashoffset={2 * Math.PI * 30 * (1 - 0.74)} transform="rotate(-90 36 36)" />
            </svg>
          </div>
          <div className="rtp-score-card-list">
            {SCORE_ITEMS.map(item => {
              const s = STATUS_STYLES[item.status];
              return (
                <div key={item.label} className="rtp-score-card-row">
                  <span>{item.label}</span>
                  <span className="rtp-score-card-badge" style={{ color: s.color, background: s.bg }}><s.Icon size={11} strokeWidth={3} /> {item.note}</span>
                </div>
              );
            })}
          </div>
          <button type="button" className="rtp-score-card-btn" onClick={() => navigate('/ats-checker')}>Run ATS Check <ArrowRight size={15} /></button>
        </div>
      </div>
    </div>
  </section>
);

// ── Quick improvement grid ────────────────────────────────────────────────────
const ImprovementGrid = ({ navigate }) => (
  <section className="rtp-section">
    <SectionHeading title="Fix the things that matter most" subtitle="Six practical upgrades that make the biggest difference to your callback rate." />
    <div className="rtp-improve-grid">
      {IMPROVEMENT_CARDS.map(c => (
        <button type="button" key={c.title} className="rtp-improve-card" onClick={() => navigate(c.to)}>
          <div className="rtp-improve-icon"><c.Icon size={22} /></div>
          <div className="rtp-improve-title">{c.title}</div>
          <p className="rtp-improve-text">{c.text}</p>
          <span className="rtp-improve-cta">{c.cta} <ArrowRight size={13} /></span>
        </button>
      ))}
    </div>
  </section>
);

// ── Before vs After ───────────────────────────────────────────────────────────
const BeforeAfterSection = ({ navigate }) => (
  <section className="rtp-section rtp-section-tint">
    <SectionHeading title="Show impact, not just responsibility" subtitle="The easiest resume upgrade: add action, scope, and result to every bullet." />
    <div className="rtp-ba-list">
      {BEFORE_AFTER.map(pair => (
        <div key={pair.before} className="rtp-ba-row">
          <div className="rtp-ba-col rtp-ba-before">
            <div className="rtp-ba-label">Before</div>
            <p>{pair.before}</p>
          </div>
          <ArrowRight size={18} className="rtp-ba-arrow" />
          <div className="rtp-ba-col rtp-ba-after">
            <div className="rtp-ba-label">After</div>
            <p>{pair.after}</p>
          </div>
        </div>
      ))}
    </div>
    <div className="rtp-center-cta">
      <button type="button" className="rtp-btn-primary" onClick={() => navigate('/build/resume')}><Sparkles size={16} /> Generate Better Bullet Points</button>
    </div>
  </section>
);

// ── Top 10 recruiter rules ────────────────────────────────────────────────────
const RulesSection = () => (
  <section className="rtp-section">
    <SectionHeading title="Top 10 recruiter rules" subtitle="The habits that separate resumes that get read from resumes that get skipped." />
    <div className="rtp-rules-grid">
      {RECRUITER_RULES.map((r, i) => (
        <div key={r.title} className="rtp-rule-card">
          <div className="rtp-rule-num">{String(i + 1).padStart(2, '0')}</div>
          <div className="rtp-rule-title">{r.title}</div>
          <p className="rtp-rule-caption">{r.caption}</p>
        </div>
      ))}
    </div>
  </section>
);

// ── Common mistakes comparison ────────────────────────────────────────────────
const MistakesSection = () => (
  <section className="rtp-section rtp-section-tint">
    <SectionHeading title="Common mistakes, section by section" subtitle="Real examples of what to avoid — and how to fix it." />
    <div className="rtp-mistake-list">
      {MISTAKE_PAIRS.map(m => (
        <div key={m.category} className="rtp-mistake-row">
          <div className="rtp-mistake-category">{m.category}</div>
          <div className="rtp-mistake-cols">
            <div className="rtp-mistake-col bad">
              <div className="rtp-mistake-label"><X size={13} /> Mistake</div>
              <p>“{m.mistake}”</p>
            </div>
            <div className="rtp-mistake-col good">
              <div className="rtp-mistake-label"><Check size={13} /> Better</div>
              <p>“{m.better}”</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

// ── Resume section checklist ──────────────────────────────────────────────────
const SectionChecklistSection = ({ navigate }) => (
  <section className="rtp-section">
    <SectionHeading title="Is Every Resume Section Strong?" subtitle="Run through each section and check what recruiters expect to see." />
    <div className="rtp-check-grid">
      {SECTION_CHECKLIST.map(s => (
        <div key={s.title} className="rtp-check-card">
          <div className="rtp-check-icon"><s.Icon size={20} /></div>
          <div className="rtp-check-title">{s.title}</div>
          <div className="rtp-check-block">
            <div className="rtp-check-block-label good">What recruiters expect</div>
            <p>{s.expect}</p>
          </div>
          <div className="rtp-check-block">
            <div className="rtp-check-block-label bad">Common mistakes</div>
            <p>{s.mistake}</p>
          </div>
          <button type="button" className="rtp-check-cta" onClick={() => navigate('/build/resume')}>AI can improve this <ArrowRight size={13} /></button>
        </div>
      ))}
    </div>
  </section>
);

// ── ATS friendly checklist ────────────────────────────────────────────────────
const AtsChecklistSection = () => {
  const printRef = useRef(null);
  const handlePrint = () => window.print();
  return (
    <section className="rtp-section rtp-section-tint">
      <SectionHeading title="ATS-friendly checklist" subtitle="Run through this before every application." />
      <div className="rtp-print-card" ref={printRef} id="rtp-print-checklist">
        <div className="rtp-print-grid">
          {ATS_CHECKLIST_ITEMS.map(item => (
            <div key={item} className="rtp-print-item"><Check size={15} strokeWidth={3} /> {item}</div>
          ))}
        </div>
        <button type="button" className="rtp-btn-secondary rtp-no-print" onClick={handlePrint}><Printer size={15} /> Download Printable Checklist</button>
      </div>
    </section>
  );
};

// ── AI writing assistant slider ───────────────────────────────────────────────
const AIWritingSection = ({ navigate }) => {
  const [index, setIndex] = useState(0);
  const ex = AI_EXAMPLES[index];
  const go = (dir) => setIndex(i => (i + dir + AI_EXAMPLES.length) % AI_EXAMPLES.length);

  return (
    <section className="rtp-section">
      <SectionHeading title="See the AI writing assistant in action" subtitle="Every weak bullet goes through the same three-step upgrade." />
      <div className="rtp-ai-card">
        <div className="rtp-ai-flow">
          <div className="rtp-ai-step weak">
            <div className="rtp-ai-step-label">Weak Bullet</div>
            <p>{ex.weak}</p>
          </div>
          <ChevronDown size={18} className="rtp-ai-flow-arrow" />
          <div className="rtp-ai-step suggestion">
            <div className="rtp-ai-step-label">AI Suggestion</div>
            <p>{ex.suggestion}</p>
          </div>
          <ChevronDown size={18} className="rtp-ai-flow-arrow" />
          <div className="rtp-ai-step approved">
            <div className="rtp-ai-step-label">Recruiter Approved</div>
            <p>{ex.approved}</p>
          </div>
        </div>
        <div className="rtp-ai-nav">
          <button type="button" className="rtp-ai-nav-btn" onClick={() => go(-1)} aria-label="Previous example"><ChevronLeft size={16} /></button>
          <div className="rtp-ai-dots">
            {AI_EXAMPLES.map((_, i) => <span key={i} className={`rtp-ai-dot${i === index ? ' active' : ''}`} onClick={() => setIndex(i)} />)}
          </div>
          <button type="button" className="rtp-ai-nav-btn" onClick={() => go(1)} aria-label="Next example"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div className="rtp-center-cta">
        <button type="button" className="rtp-btn-primary" onClick={() => navigate('/build/resume')}><Sparkles size={16} /> Rewrite My Resume</button>
      </div>
    </section>
  );
};

// ── Resume examples ───────────────────────────────────────────────────────────
const ResumeExamplesSection = ({ user, navigate, onPreview }) => {
  const useTemplate = (templateId) => {
    if (user) navigate(`/builder/resume/new?template=${templateId}`);
    else navigate(`/build/resume?template=${templateId}`);
  };

  return (
    <section className="rtp-section rtp-section-tint">
      <SectionHeading title="Resume examples that pass ATS" subtitle="Three real templates, scored, ready to preview or use." />
      <div className="rtp-example-grid">
        {RESUME_EXAMPLE_ROLES.map(item => {
          const tmpl = TEMPLATES.find(t => t.id === item.templateId);
          if (!tmpl) return null;
          const preview = buildShowcaseResume(tmpl);
          return (
            <div key={item.role} className="rtp-example-card">
              <div className="rtp-example-preview">
                <div className="rtp-example-badge">{item.score}% ATS Score</div>
                <div className="rtp-example-preview-frame">
                  <ResumePreview data={preview} id={`rtp-example-${item.templateId}`} singlePage />
                </div>
              </div>
              <div className="rtp-example-body">
                <div className="rtp-example-role">{item.role}</div>
                <div className="rtp-example-tmpl">{tmpl.name} template</div>
                <div className="rtp-example-actions">
                  <button type="button" className="rtp-btn-secondary" onClick={() => onPreview({ tmpl, buildFn: buildShowcaseResume })}><Eye size={14} /> Preview</button>
                  <button type="button" className="rtp-btn-primary rtp-example-use" onClick={() => useTemplate(tmpl.id)}>Use Template</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const PreviewModal = ({ item, onClose }) => {
  if (!item) return null;
  const { tmpl, buildFn } = item;
  const preview = buildFn(tmpl);
  return (
    <div className="rtp-modal-overlay" onClick={onClose}>
      <div className="rtp-modal-card" onClick={e => e.stopPropagation()}>
        <button type="button" className="rtp-modal-close" onClick={onClose} aria-label="Close preview"><X size={18} /></button>
        <div className="rtp-modal-preview">
          <div style={{ width: 794 }}>
            <ResumePreview data={preview} id={`rtp-modal-${tmpl.id}`} />
          </div>
        </div>
        <div className="rtp-modal-footer">
          <div className="rtp-modal-name">{tmpl.name}</div>
          <div className="rtp-modal-desc">{tmpl.desc}</div>
        </div>
      </div>
    </div>
  );
};

// ── Recruiter quotes ──────────────────────────────────────────────────────────
const QuotesSection = () => (
  <section className="rtp-section">
    <SectionHeading title="What recruiters actually say" />
    <div className="rtp-quotes-grid">
      {RECRUITER_QUOTES.map(q => (
        <div key={q.quote} className="rtp-quote-card">
          <Quote size={20} className="rtp-quote-icon" />
          <p>{q.quote}</p>
          <div className="rtp-quote-attr">
            <div className="rtp-quote-role">{q.role}</div>
            {q.company && <div className="rtp-quote-company">{q.company}</div>}
          </div>
        </div>
      ))}
    </div>
  </section>
);

// ── FAQ ────────────────────────────────────────────────────────────────────────
const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="rtp-section rtp-section-tint">
      <SectionHeading title="Frequently asked questions" />
      <div className="rtp-faq-list">
        {FAQS.map(([q, a], i) => {
          const isOpen = openIdx === i;
          return (
            <div key={q} className={`rtp-faq-item${isOpen ? ' open' : ''}`}>
              <button type="button" className="rtp-faq-trigger" aria-expanded={isOpen} onClick={() => setOpenIdx(isOpen ? null : i)}>
                <span>{q}</span>
                <span className="rtp-faq-icon">
                  <span className="bar h" />
                  <motion.span className="bar v" animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.25, ease: 'easeInOut' }} />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: 'hidden' }}>
                    <div className="rtp-faq-answer">{a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ── Final CTA ──────────────────────────────────────────────────────────────────
const FinalCTASection = ({ user, navigate }) => (
  <section className="rtp-cta-outer">
    <div className="rtp-cta-card">
      <h2>Ready to Build a Better Resume?</h2>
      <p>Create your resume, optimize it for ATS, tailor it for every job, and improve it with AI — all in one place.</p>
      <div className="rtp-cta-actions">
        <button type="button" className="rtp-btn-primary" onClick={() => navigate(user ? '/dashboard' : '/build/resume')}><Sparkles size={16} /> Create Resume Free</button>
        <button type="button" className="rtp-btn-ghost-dark" onClick={() => navigate('/ats-checker')}>Run ATS Check</button>
      </div>
    </div>
  </section>
);

// ── Main page ────────────────────────────────────────────────────────────────
export const ResumeTipsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [previewItem, setPreviewItem] = useState(null);

  usePageSEO({
    title: 'Resume Tips - MarsResume | Build a Stronger ATS-Friendly Resume',
    description: 'Practical resume tips for structure, keywords, achievements, formatting, and final checks before applying — with real before/after examples.',
    canonical: '/resume-tips',
  });

  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);

  return (
    <div className="rtp-page">
      <LandingStyles />
      <ResumeTipsStyles />
      <MarketingHeader />

      <HeroSection user={user} navigate={navigate} />
      <ImprovementGrid navigate={navigate} />
      <BeforeAfterSection navigate={navigate} />
      <RulesSection />
      <MistakesSection />
      <SectionChecklistSection navigate={navigate} />
      <AtsChecklistSection />
      <AIWritingSection navigate={navigate} />
      <ResumeExamplesSection user={user} navigate={navigate} onPreview={setPreviewItem} />
      <QuotesSection />
      <FAQSection />
      <FinalCTASection user={user} navigate={navigate} />

      <MarketingFooter navigate={navigate} goTo={goTo} />

      <PreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />
    </div>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────
const ResumeTipsStyles = () => (
  <style>{`
    .rtp-page { min-height: 100vh; background: #F8FAFC; font-family: 'Inter', sans-serif; color: #111827; overflow-x: hidden; }

    /* Buttons */
    .rtp-btn-primary { background: #EF1D28; color: white; border: none; border-radius: 12px; padding: 13px 24px; font-weight: 700; font-size: 14.5px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; transition: background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease; box-shadow: 0 4px 16px rgba(239,29,40,0.25); }
    .rtp-btn-primary:hover { background: #D91620; transform: translateY(-2px); box-shadow: 0 10px 26px rgba(239,29,40,0.35); }
    .rtp-btn-ghost-dark { background: rgba(255,255,255,0.06); border: 1.5px solid rgba(255,255,255,0.2); color: white; border-radius: 12px; padding: 13px 24px; font-weight: 700; font-size: 14.5px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; transition: all 0.18s ease; }
    .rtp-btn-ghost-dark:hover { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.35); }
    .rtp-btn-secondary { background: white; border: 1.5px solid #E5E7EB; color: #374151; border-radius: 10px; padding: 10px 18px; font-weight: 700; font-size: 13px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 7px; transition: all 0.18s ease; }
    .rtp-btn-secondary:hover { border-color: #CBD5E1; background: #F9FAFB; }

    /* Section heading */
    .rtp-section { padding: 68px 48px; max-width: 1160px; margin: 0 auto; }
    .rtp-section-tint { background: #FAFBFF; max-width: none; padding-left: 48px; padding-right: 48px; }
    .rtp-section-tint > * { max-width: 1160px; margin-left: auto; margin-right: auto; }
    .rtp-sec-heading { margin-bottom: 40px; text-align: center; }
    .rtp-sec-heading h2 { margin: 0; font-size: 30px; font-weight: 900; color: #0F172A; letter-spacing: -0.7px; }
    .rtp-sec-heading p { margin: 10px auto 0; color: #64748B; font-size: 15px; line-height: 1.65; max-width: 600px; }
    .rtp-eyebrow { display: inline-flex; align-items: center; gap: 6px; background: #FFF1F2; border: 1px solid #FECACA; border-radius: 999px; padding: 5px 13px; font-size: 11px; font-weight: 700; color: #DC2626; letter-spacing: 0.4px; text-transform: uppercase; margin-bottom: 12px; }
    .rtp-eyebrow-dark { background: rgba(239,29,40,0.16); border-color: rgba(239,29,40,0.36); color: #FCA5A5; }
    .rtp-center-cta { text-align: center; margin-top: 36px; }

    /* Hero */
    .rtp-hero { background: linear-gradient(160deg,#070E1A 0%,#0D1120 50%,#0A1420 100%); padding: 84px 48px 92px; position: relative; overflow: hidden; }
    .rtp-hero-grid-bg { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.06) 1px,transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .rtp-hero-glow { position: absolute; top: 10%; right: 10%; width: 500px; height: 400px; background: radial-gradient(ellipse, rgba(239,29,40,0.12) 0%, transparent 70%); pointer-events: none; }
    .rtp-hero-inner { max-width: 1160px; margin: 0 auto; display: flex; align-items: center; gap: 56px; position: relative; }
    .rtp-hero-left { flex: 0 0 48%; }
    .rtp-hero-left h1 { margin: 0 0 20px; font-size: 46px; line-height: 1.1; font-weight: 900; letter-spacing: -1.4px; color: white; }
    .rtp-hero-left p { margin: 0 0 30px; font-size: 16.5px; line-height: 1.7; color: #94A3B8; max-width: 470px; }
    .rtp-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }
    .rtp-hero-right { flex: 1; display: flex; justify-content: center; }

    .rtp-score-card { width: 100%; max-width: 380px; background: white; border-radius: 22px; padding: 28px; box-shadow: 0 30px 70px rgba(0,0,0,0.3); }
    .rtp-score-card-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 22px; }
    .rtp-score-card-label { font-size: 12px; font-weight: 800; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 6px; }
    .rtp-score-card-value { font-size: 40px; font-weight: 900; color: #0F172A; letter-spacing: -1.5px; }
    .rtp-score-card-list { display: flex; flex-direction: column; margin-bottom: 22px; }
    .rtp-score-card-row { display: flex; align-items: center; justify-content: space-between; padding: 11px 0; border-top: 1px solid #F1F5F9; font-size: 13.5px; font-weight: 600; color: #334155; }
    .rtp-score-card-row:first-child { border-top: none; }
    .rtp-score-card-badge { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 800; padding: 3px 9px; border-radius: 999px; }
    .rtp-score-card-btn { width: 100%; border: none; background: linear-gradient(135deg,#0F172A,#1E293B); color: white; border-radius: 12px; padding: 13px; font-weight: 800; font-size: 14px; cursor: pointer; font-family: inherit; display: flex; align-items: center; justify-content: center; gap: 8px; transition: opacity 0.18s ease; }
    .rtp-score-card-btn:hover { opacity: 0.9; }

    /* Improvement grid */
    .rtp-improve-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 20px; }
    .rtp-improve-card { text-align: left; background: white; border: 1px solid #E5E7EB; border-radius: 22px; padding: 26px; cursor: pointer; font-family: inherit; transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease; }
    .rtp-improve-card:hover { box-shadow: 0 16px 40px rgba(15,23,42,0.09); border-color: #FBD3D5; transform: translateY(-3px); }
    .rtp-improve-icon { width: 46px; height: 46px; border-radius: 13px; background: #FFF1F2; color: #EF1D28; display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
    .rtp-improve-title { font-size: 16px; font-weight: 800; color: #0F172A; margin-bottom: 8px; }
    .rtp-improve-text { margin: 0 0 16px; color: #64748B; font-size: 13.5px; line-height: 1.6; }
    .rtp-improve-cta { display: inline-flex; align-items: center; gap: 5px; color: #EF1D28; font-size: 13px; font-weight: 800; }

    /* Before / After */
    .rtp-ba-list { display: flex; flex-direction: column; gap: 16px; max-width: 980px; margin: 0 auto; }
    .rtp-ba-row { display: grid; grid-template-columns: 1fr 32px 1fr; align-items: center; gap: 8px; }
    .rtp-ba-col { border-radius: 18px; padding: 20px 22px; }
    .rtp-ba-before { background: #FFF7F7; border: 1.5px solid #FECACA; }
    .rtp-ba-after { background: #F0FDF4; border: 1.5px solid #BBF7D0; }
    .rtp-ba-label { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 8px; }
    .rtp-ba-before .rtp-ba-label { color: #DC2626; }
    .rtp-ba-after .rtp-ba-label { color: #059669; }
    .rtp-ba-col p { margin: 0; font-size: 14px; line-height: 1.6; }
    .rtp-ba-before p { color: #7F1D1D; }
    .rtp-ba-after p { color: #14532D; font-weight: 600; }
    .rtp-ba-arrow { color: #CBD5E1; justify-self: center; }

    /* Rules */
    .rtp-rules-grid { display: grid; grid-template-columns: repeat(5, minmax(0,1fr)); gap: 16px; }
    .rtp-rule-card { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 22px; transition: box-shadow 0.2s ease, border-color 0.2s ease; }
    .rtp-rule-card:hover { box-shadow: 0 14px 34px rgba(15,23,42,0.08); border-color: #FBD3D5; }
    .rtp-rule-num { font-size: 22px; font-weight: 900; color: #FBD3D5; margin-bottom: 10px; letter-spacing: -0.5px; }
    .rtp-rule-title { font-size: 14px; font-weight: 800; color: #0F172A; margin-bottom: 6px; line-height: 1.35; }
    .rtp-rule-caption { margin: 0; font-size: 12px; color: #64748B; line-height: 1.55; }

    /* Mistakes comparison */
    .rtp-mistake-list { display: flex; flex-direction: column; gap: 22px; max-width: 980px; margin: 0 auto; }
    .rtp-mistake-category { font-size: 12px; font-weight: 800; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 10px; }
    .rtp-mistake-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .rtp-mistake-col { border-radius: 16px; padding: 18px 20px; }
    .rtp-mistake-col.bad { background: #FFF7F7; border: 1.5px solid #FECACA; }
    .rtp-mistake-col.good { background: #F0FDF4; border: 1.5px solid #BBF7D0; }
    .rtp-mistake-label { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 8px; }
    .rtp-mistake-col.bad .rtp-mistake-label { color: #DC2626; }
    .rtp-mistake-col.good .rtp-mistake-label { color: #059669; }
    .rtp-mistake-col p { margin: 0; font-size: 13.5px; line-height: 1.6; }
    .rtp-mistake-col.bad p { color: #7F1D1D; }
    .rtp-mistake-col.good p { color: #14532D; font-weight: 600; }

    /* Section checklist */
    .rtp-check-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 20px; }
    .rtp-check-card { background: white; border: 1px solid #E5E7EB; border-radius: 22px; padding: 24px; transition: box-shadow 0.2s ease, border-color 0.2s ease; }
    .rtp-check-card:hover { box-shadow: 0 16px 40px rgba(15,23,42,0.08); border-color: #E2E8F0; }
    .rtp-check-icon { width: 42px; height: 42px; border-radius: 12px; background: #F1F5F9; color: #0F172A; display: flex; align-items: center; justify-content: center; margin-bottom: 14px; }
    .rtp-check-title { font-size: 16px; font-weight: 800; color: #0F172A; margin-bottom: 14px; }
    .rtp-check-block { margin-bottom: 12px; }
    .rtp-check-block-label { font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px; }
    .rtp-check-block-label.good { color: #059669; }
    .rtp-check-block-label.bad { color: #DC2626; }
    .rtp-check-block p { margin: 0; font-size: 12.5px; color: #475569; line-height: 1.55; }
    .rtp-check-cta { display: inline-flex; align-items: center; gap: 5px; border: none; background: none; color: #EF1D28; font-size: 12.5px; font-weight: 800; cursor: pointer; font-family: inherit; padding: 8px 0 0; }

    /* ATS printable checklist */
    .rtp-print-card { max-width: 760px; margin: 0 auto; background: white; border: 1px solid #E5E7EB; border-radius: 22px; padding: 32px; text-align: center; box-shadow: 0 10px 32px rgba(15,23,42,0.05); }
    .rtp-print-grid { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 12px; margin-bottom: 26px; text-align: left; }
    .rtp-print-item { display: flex; align-items: center; gap: 9px; background: #ECFDF5; border: 1px solid #BBF7D0; color: #065F46; border-radius: 11px; padding: 12px 14px; font-size: 13.5px; font-weight: 700; }

    /* AI writing slider */
    .rtp-ai-card { max-width: 560px; margin: 0 auto; background: white; border: 1px solid #E5E7EB; border-radius: 22px; padding: 30px; box-shadow: 0 10px 32px rgba(15,23,42,0.05); }
    .rtp-ai-flow { display: flex; flex-direction: column; align-items: center; gap: 10px; margin-bottom: 22px; }
    .rtp-ai-step { width: 100%; border-radius: 14px; padding: 16px 18px; }
    .rtp-ai-step-label { font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 6px; }
    .rtp-ai-step p { margin: 0; font-size: 13.5px; line-height: 1.55; }
    .rtp-ai-step.weak { background: #FFF7F7; border: 1.5px solid #FECACA; }
    .rtp-ai-step.weak .rtp-ai-step-label { color: #DC2626; }
    .rtp-ai-step.weak p { color: #7F1D1D; text-decoration: line-through; }
    .rtp-ai-step.suggestion { background: #F5F3FF; border: 1.5px solid #DDD6FE; }
    .rtp-ai-step.suggestion .rtp-ai-step-label { color: #7C3AED; }
    .rtp-ai-step.suggestion p { color: #4C1D95; }
    .rtp-ai-step.approved { background: #F0FDF4; border: 1.5px solid #BBF7D0; }
    .rtp-ai-step.approved .rtp-ai-step-label { color: #059669; }
    .rtp-ai-step.approved p { color: #14532D; font-weight: 600; }
    .rtp-ai-flow-arrow { color: #CBD5E1; }
    .rtp-ai-nav { display: flex; align-items: center; justify-content: center; gap: 16px; }
    .rtp-ai-nav-btn { width: 34px; height: 34px; border-radius: 50%; border: 1.5px solid #E5E7EB; background: white; color: #374151; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.18s ease; }
    .rtp-ai-nav-btn:hover { border-color: #CBD5E1; background: #F9FAFB; }
    .rtp-ai-dots { display: flex; gap: 7px; }
    .rtp-ai-dot { width: 7px; height: 7px; border-radius: 50%; background: #E2E8F0; cursor: pointer; }
    .rtp-ai-dot.active { background: #EF1D28; width: 20px; border-radius: 4px; transition: width 0.18s ease; }

    /* Resume examples */
    .rtp-example-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 20px; }
    .rtp-example-card { background: white; border: 1px solid #E5E7EB; border-radius: 22px; overflow: hidden; box-shadow: 0 10px 32px rgba(15,23,42,0.05); transition: box-shadow 0.2s ease, border-color 0.2s ease; }
    .rtp-example-card:hover { box-shadow: 0 20px 48px rgba(15,23,42,0.1); border-color: #E2E8F0; }
    .rtp-example-preview { position: relative; background: linear-gradient(180deg,#F1F5F9 0%,#E2E8F0 100%); padding: 20px 20px 0; height: 280px; overflow: hidden; display: flex; justify-content: center; }
    .rtp-example-badge { position: absolute; top: 12px; left: 12px; z-index: 2; background: linear-gradient(135deg,#059669,#10B981); color: white; border-radius: 7px; padding: 4px 10px; font-size: 10.5px; font-weight: 800; }
    .rtp-example-preview-frame { width: 88%; }
    .rtp-example-body { padding: 18px 20px 20px; }
    .rtp-example-role { font-size: 16px; font-weight: 800; color: #0F172A; margin-bottom: 3px; }
    .rtp-example-tmpl { font-size: 12.5px; color: #94A3B8; margin-bottom: 16px; }
    .rtp-example-actions { display: flex; gap: 8px; }
    .rtp-example-actions .rtp-btn-secondary, .rtp-example-use { flex: 1; justify-content: center; padding: 10px; }

    /* Preview modal */
    .rtp-modal-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(10,15,25,0.6); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; padding: 24px; }
    .rtp-modal-card { background: white; border-radius: 22px; width: 100%; max-width: 620px; max-height: 88vh; overflow: hidden; display: flex; flex-direction: column; position: relative; box-shadow: 0 40px 100px rgba(0,0,0,0.4); }
    .rtp-modal-close { position: absolute; top: 14px; right: 14px; z-index: 5; border: none; background: rgba(255,255,255,0.9); color: #374151; width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 4px 14px rgba(0,0,0,0.15); }
    .rtp-modal-preview { background: #F1F5F9; overflow: auto; flex: 1; display: flex; justify-content: center; padding: 24px; transform: scale(0.62); transform-origin: top center; }
    .rtp-modal-footer { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 18px 22px; border-top: 1px solid #F1F5F9; }
    .rtp-modal-name { font-size: 15px; font-weight: 800; color: #111827; }
    .rtp-modal-desc { font-size: 12.5px; color: #64748B; }

    /* Quotes */
    .rtp-quotes-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 20px; }
    .rtp-quote-card { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 26px; }
    .rtp-quote-icon { color: #FBD3D5; margin-bottom: 12px; }
    .rtp-quote-card p { margin: 0 0 18px; font-size: 15px; color: #1F2937; line-height: 1.6; font-weight: 600; }
    .rtp-quote-role { font-size: 13px; font-weight: 800; color: #0F172A; }
    .rtp-quote-company { font-size: 12px; color: #94A3B8; margin-top: 1px; }

    /* FAQ */
    .rtp-faq-list { display: flex; flex-direction: column; gap: 12px; max-width: 780px; margin: 0 auto; }
    .rtp-faq-item { background: white; border: 1.5px solid #E5E7EB; border-radius: 16px; overflow: hidden; transition: border-color 0.25s ease, box-shadow 0.25s ease; }
    .rtp-faq-item.open { border-color: #FECACA; box-shadow: 0 10px 30px rgba(239,29,40,0.08); }
    .rtp-faq-trigger { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 24px; background: transparent; border: none; cursor: pointer; text-align: left; font-family: inherit; }
    .rtp-faq-trigger span { font-size: 15.5px; font-weight: 700; color: #111827; line-height: 1.4; }
    .rtp-faq-icon { position: relative; width: 30px; height: 30px; border-radius: 9px; background: #F9FAFB; border: 1px solid #E5E7EB; flex-shrink: 0; transition: background 0.25s ease, border-color 0.25s ease; }
    .rtp-faq-item.open .rtp-faq-icon { background: #FFF1F2; border-color: #FECDD3; }
    .rtp-faq-icon .bar { position: absolute; top: 50%; left: 50%; background: #6B7280; border-radius: 2px; }
    .rtp-faq-icon .bar.h { width: 12px; height: 2px; margin: -1px 0 0 -6px; }
    .rtp-faq-icon .bar.v { width: 2px; height: 12px; margin: -6px 0 0 -1px; }
    .rtp-faq-item.open .rtp-faq-icon .bar { background: #EF1D28; }
    .rtp-faq-answer { padding: 0 24px 22px; font-size: 14.5px; color: #6B7280; line-height: 1.8; }

    /* Final CTA */
    .rtp-cta-outer { padding: 20px 48px 96px; }
    .rtp-cta-card { max-width: 900px; margin: 0 auto; background: linear-gradient(150deg, #060D1A 0%, #0D1120 50%, #0A1428 100%); border-radius: 28px; padding: 64px 48px; border: 1px solid #1A2A3E; text-align: center; }
    .rtp-cta-card h2 { margin: 0 0 14px; font-size: 34px; font-weight: 900; color: white; letter-spacing: -0.7px; }
    .rtp-cta-card p { margin: 0 auto 30px; color: #94A3B8; font-size: 15.5px; line-height: 1.7; max-width: 560px; }
    .rtp-cta-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

    /* Print */
    @media print {
      body * { visibility: hidden; }
      #rtp-print-checklist, #rtp-print-checklist * { visibility: visible; }
      #rtp-print-checklist { position: absolute; top: 0; left: 0; width: 100%; box-shadow: none; border: none; }
      .rtp-no-print { display: none !important; }
    }

    /* Responsive */
    @media (max-width: 1100px) {
      .rtp-improve-grid, .rtp-check-grid, .rtp-example-grid, .rtp-quotes-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .rtp-rules-grid { grid-template-columns: repeat(3, minmax(0,1fr)); }
    }
    @media (max-width: 900px) {
      .rtp-hero { padding: 60px 24px 72px; }
      .rtp-hero-inner { flex-direction: column; gap: 44px; text-align: center; }
      .rtp-hero-left { flex: none; }
      .rtp-hero-left p { margin-left: auto; margin-right: auto; }
      .rtp-hero-ctas { justify-content: center; }
      .rtp-hero-left h1 { font-size: 36px; }
      .rtp-section { padding: 52px 24px; }
      .rtp-section-tint { padding-left: 24px; padding-right: 24px; }
      .rtp-ba-row, .rtp-mistake-cols { grid-template-columns: 1fr; }
      .rtp-ba-arrow { transform: rotate(90deg); justify-self: flex-start; margin-left: 22px; }
      .rtp-print-grid { grid-template-columns: 1fr; }
    }
    @media (max-width: 640px) {
      .rtp-improve-grid, .rtp-check-grid, .rtp-example-grid, .rtp-quotes-grid, .rtp-rules-grid { grid-template-columns: 1fr; }
      .rtp-hero-left h1 { font-size: 28px; letter-spacing: -1px; }
      .rtp-cta-card { padding: 44px 24px; }
      .rtp-cta-card h2 { font-size: 26px; }
      .rtp-score-card { padding: 22px; }
    }
  `}</style>
);
